import { computed, defineComponent, ref, watch } from '@vue/composition-api';
import QueryRow from '../query/QueryRowV2.vue';
import Dropdown from 'components/Butterfly/Dropdown/Dropdown.vue';
import SegmentMenu from 'src/pages/dashboard/SegmentMenu.vue';
import { getContainedQueries } from './ThemeBuilder.utils';
import { difference, isEmpty, union } from 'lodash';
import QueryAPI from 'src/api/query';
import QueryUtils from 'src/utils/query';
import Icon from 'src/components/Icon.vue';
const FilterBar = defineComponent({
    components: {
        QueryRow,
        Dropdown,
        SegmentMenu,
        Icon,
    },
    props: {
        queryId: { type: Number, required: true },
        queryScope: { type: String, required: true },
        queryRows: { type: Array, required: true },
        conceptList: { type: Array, required: false, default: () => [] },
        savedQueries: { type: Array, required: false, default: () => [] },
        currentSite: { type: Object, required: false, default: null },
        currentProject: { type: Object, required: false, default: null },
        currentAnalysis: { type: Object, required: false, default: null },
        // Allow structured fields in segment menu
        allowStructured: { type: Boolean, required: false, default: true },
        // Allow custom text addition in segment menu
        allowText: { type: Boolean, required: false, default: true },
        // Allow operators in segment menu
        allowOperators: { type: Boolean, required: false, default: true },
        // Allow themes in segment menu
        allowThemes: { type: Boolean, required: false, default: true },
        // Allow concepts in segment menu
        allowConcepts: { type: Boolean, required: false, default: true },
        // Allow date fields in segment menu
        allowDates: { type: Boolean, required: false, default: true },
        collapsable: { type: Boolean, required: false, default: true },
        location: { type: String, required: true },
        filterWarnings: { type: Array, required: false, default: () => [] },
    },
    setup(props, { emit }) {
        const textVariants = ref({});
        const collapsed = ref(false);
        watch(collapsed, () => {
            emit('collapse-change', collapsed.value);
        });
        const firstRow = computed(() => {
            return props.queryRows.findIndex((q) => q['type'] !== 'all_data');
        });
        const setQueryRows = (rows) => {
            emit('set-query-rows', rows);
        };
        const deleteRow = (index) => {
            const rows = props.queryRows.filter((_, i) => i !== index);
            setQueryRows(rows);
        };
        const updateRow = (index, change) => {
            const rows = props.queryRows.map((v, i) => {
                return i === index
                    ? { ...v, ...change }
                    : v;
            });
            setQueryRows(rows);
        };
        const addRow = (row) => {
            const { queryRows } = props;
            const existingRow = queryRows.find(r => {
                return row.field === r.field &&
                    (r.operator === "is" || r.operator === "is not");
            });
            // NOTE: "is" and "is not" are only used for numeric and categorical fields,
            // text type uses the operator "includes" for the same purpose.
            // so this doesn't affect adding text to the filters.
            if (existingRow) {
                if (Array.isArray(existingRow.values) && Array.isArray(row.values)) {
                    updateRow(queryRows.indexOf(existingRow), {
                        values: [...existingRow.values, ...row.values],
                    });
                }
            }
            else {
                setQueryRows([...queryRows, row]);
            }
        };
        const clearRows = () => {
            setQueryRows([]);
        };
        const allowedChildThemes = computed(() => {
            if (!props.allowThemes)
                return [];
            return props.savedQueries.filter((q) => {
                const contained = getContainedQueries(q, props.savedQueries).map(q => q.id);
                if (contained.includes(props.queryId)) {
                    // The selected theme is contained within this theme
                    // and would result in a circular reference
                    return false;
                }
                return true;
            })
                .filter((q) => q.id !== props.queryId)
                .map((q) => ({ name: q.name, id: q.id }));
        });
        const fetchQueryTextVariants = () => {
            if (!props.currentProject ||
                !props.currentSite ||
                !props.currentAnalysis)
                return;
            const botanicQuery = QueryUtils.queryRowsToBotanic(props.queryRows, props.queryScope);
            return QueryAPI.getQueryTextVariants(props.currentProject.id, props.currentSite.chrysalis_url, props.currentProject.chrysalis_ref, props.currentAnalysis.topic_framework_id, botanicQuery, props.savedQueries).then((result) => {
                textVariants.value = result;
            });
        };
        const queryTextValues = computed(() => {
            let textArr = [];
            props.queryRows.forEach((q) => {
                if (q.type === 'text') {
                    textArr = union(textArr, q.values);
                }
            });
            return textArr;
        });
        watch(queryTextValues, (newVal, oldVal) => {
            if (!oldVal)
                return;
            const diff = difference(newVal, oldVal);
            if (isEmpty(diff))
                return;
            fetchQueryTextVariants();
        }, {
            immediate: true,
        });
        return {
            firstRow,
            deleteRow,
            updateRow,
            addRow,
            clearRows,
            allowedChildThemes,
            textVariants,
            collapsed,
        };
    },
});
export default FilterBar;
